import React from "react";
import { Link } from "gatsby";
import { ReactComponent as Arrow } from "~/images/inline_svg/right_arrow.svg";
import { colors } from "~/utils/colors";
import { breakPoints } from "~/utils/variables";

type PageItem = number | "ellipsis";

const getVisiblePages = (currentPage: number, totalPages: number): PageItem[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages]);

  for (let page = currentPage - 1; page <= currentPage + 1; page++) {
    if (page >= 1 && page <= totalPages) {
      pages.add(page);
    }
  }

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= totalPages - 2) {
    pages.add(totalPages - 3);
    pages.add(totalPages - 2);
    pages.add(totalPages - 1);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result: PageItem[] = [];

  sorted.forEach((page, index) => {
    if (index > 0 && page - sorted[index - 1] > 1) {
      result.push("ellipsis");
    }
    result.push(page);
  });

  return result;
};

const defaultBuildPath = (basePath: string, page: number) =>
  page === 1 ? basePath : `${basePath.replace(/\/$/, "")}/${page}`;

type Props = {
  currentPage: number;
  totalPages: number;
  basePath: string;
  buildPath?: (basePath: string, page: number) => string;
  marginTop?: number;
};

export const Pager: React.FC<Props> = ({
  currentPage,
  totalPages,
  basePath,
  buildPath = defaultBuildPath,
  marginTop = 30,
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const pages = getVisiblePages(currentPage, totalPages);
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <>
      <style jsx>{`
        .pager {
          margin: ${marginTop}px 0 0;
          padding: 0 30px;
        }

        .pagerList {
          align-items: center;
          display: flex;
          justify-content: center;
          list-style: none;
          margin: 0;
          padding: 0;
        }

        .arrowItem {
          flex-shrink: 0;
        }

        .arrowItem.previous {
          margin-right: 20px;
        }

        .arrowItem.next {
          margin-left: 20px;
        }

        .arrowItem :global(a),
        .arrowItem .arrowDisabled {
          align-items: center;
          background-color: ${colors.orange};
          border-radius: 50%;
          display: flex;
          height: 44px;
          justify-content: center;
          text-decoration: none;
          transition: background-color 0.2s ease, opacity 0.2s ease;
          width: 44px;
        }

        .arrowItem :global(a):hover {
          background-color: #e55a00;
        }

        .arrowItem .arrowDisabled {
          background-color: ${colors.subOrange};
          cursor: default;
          opacity: 0.55;
          pointer-events: none;
        }

        .arrowItem :global(svg) {
          fill: ${colors.white};
          height: 14px;
          width: 8px;
        }

        .arrowItem.previous :global(svg) {
          transform: rotate(180deg);
        }

        .pageItem {
          margin: 0 2px;
        }

        .pageItem :global(a),
        .pageItem .current {
          align-items: center;
          color: ${colors.defaultText};
          display: flex;
          font-size: 16px;
          font-weight: 500;
          justify-content: center;
          min-height: 44px;
          min-width: 36px;
          padding: 0 8px 6px;
          position: relative;
          text-decoration: none;
          transition: color 0.2s ease;
        }

        .pageItem :global(a):hover {
          color: ${colors.orange};
        }

        .pageItem .current {
          color: ${colors.orange};
          font-weight: 700;
          pointer-events: none;
        }

        .pageItem .current::after {
          background-color: ${colors.orange};
          bottom: 4px;
          content: "";
          height: 2px;
          left: 50%;
          position: absolute;
          transform: translateX(-50%);
          width: 18px;
        }

        .ellipsis {
          color: ${colors.defaultText};
          font-size: 16px;
          font-weight: 500;
          letter-spacing: 1px;
          min-width: 36px;
          padding: 0 4px 6px;
          text-align: center;
          user-select: none;
        }

        @media (${breakPoints.sp}) {
          .pager {
            padding: 0 16px;
          }

          .arrowItem.previous {
            margin-right: 12px;
          }

          .arrowItem.next {
            margin-left: 12px;
          }

          .arrowItem :global(a),
          .arrowItem .arrowDisabled {
            height: 40px;
            width: 40px;
          }

          .pageItem :global(a),
          .pageItem .current {
            font-size: 15px;
            min-width: 32px;
            padding: 0 6px 6px;
          }
        }
      `}</style>
      <nav className="pager" aria-label="ページ送り">
        <ul className="pagerList">
          <li className="arrowItem previous">
            {hasPrevious ? (
              <Link to={buildPath(basePath, currentPage - 1)} aria-label={`${currentPage - 1}ページ目へ`}>
                <Arrow />
              </Link>
            ) : (
              <span className="arrowDisabled" aria-hidden="true">
                <Arrow />
              </span>
            )}
          </li>
          {pages.map((page, index) =>
            page === "ellipsis" ? (
              <li key={`ellipsis-${index}`} className="pageItem" aria-hidden="true">
                <span className="ellipsis">...</span>
              </li>
            ) : (
              <li key={page} className="pageItem">
                {page === currentPage ? (
                  <span className="current" aria-current="page">
                    {page}
                  </span>
                ) : (
                  <Link to={buildPath(basePath, page)}>{page}</Link>
                )}
              </li>
            ),
          )}
          <li className="arrowItem next">
            {hasNext ? (
              <Link to={buildPath(basePath, currentPage + 1)} aria-label={`${currentPage + 1}ページ目へ`}>
                <Arrow />
              </Link>
            ) : (
              <span className="arrowDisabled" aria-hidden="true">
                <Arrow />
              </span>
            )}
          </li>
        </ul>
      </nav>
    </>
  );
};
